import { Paper, Space, Text, Title } from "@mantine/core";
import { useState } from "react";
import { useToast } from "../../mantine/notifications/useToast";
import { DeleteConfirmationModal } from "../BuildingBlocks/Layout/Modals/DeleteConfirmationModal/DeleteConfirmationModal";
import { PageContent } from "../BuildingBlocks/Layout/PageContent/PageContent";
import { PageHeader } from "../BuildingBlocks/Layout/PageHeader/PageHeader";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import type { OAuthApplicationResponse } from "./ApiSettings.types";
import { useOAuthApplications } from "./hooks/useOAuthApplications";
import { useOAuthApplicationsMutations } from "./hooks/useOAuthApplicationsMutations";
import { OAuthApplicationsTable } from "./OAuthApplicationsTable/OAuthApplicationsTable";
import { RegenerateClientSecretModal } from "./RegenerateClientSecretModal/RegenerateClientSecretModal";

function ApiSettingsView() {
  const { applications, isLoading, error } = useOAuthApplications();
  const { deleteMutation } = useOAuthApplicationsMutations();
  const [applicationToDelete, setApplicationToDelete] =
    useState<OAuthApplicationResponse | null>(null);
  const [applicationIsDeleting, setApplicationIsDeleting] = useState(false);
  const [applicationToRegenerate, setApplicationToRegenerate] =
    useState<OAuthApplicationResponse | null>(null);
  const { showErrorToast } = useToast();

  async function deleteApplication() {
    if (!applicationToDelete) {
      return;
    }

    setApplicationIsDeleting(true);

    try {
      await deleteMutation.mutateAsync(applicationToDelete.id);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setApplicationIsDeleting(false);
      setApplicationToDelete(null);
    }
  }

  return (
    <>
      <PageContent className="ApiSettingsView">
        <PageHeader title="API-Einstellungen" />
        <Paper>
          <header>
            <Title order={4}>Registrierte OAuth-Applikationen</Title>
            <Text c="dimmed">
              Auflistung aller OAuth-Applikationen, die in opti.node registriert
              sind.
            </Text>
          </header>
          <Space h="lg" />
          <LoadOrError error={error} loading={isLoading}>
            {applications && (
              <OAuthApplicationsTable
                applications={applications}
                onDelete={(application) => setApplicationToDelete(application)}
                onRegenerateSecret={(application) =>
                  setApplicationToRegenerate(application)
                }
              />
            )}
          </LoadOrError>
        </Paper>
      </PageContent>
      {applicationToDelete && (
        <DeleteConfirmationModal
          isLoading={applicationIsDeleting}
          name={applicationToDelete?.name}
          opened={!!applicationToDelete}
          toggleModal={() => setApplicationToDelete(null)}
          onConfirm={deleteApplication}
        />
      )}
      <RegenerateClientSecretModal
        application={applicationToRegenerate}
        isOpen={!!applicationToRegenerate}
        onClose={() => setApplicationToRegenerate(null)}
      />
    </>
  );
}

export { ApiSettingsView };
